import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCurrentUser } from '@/hooks';
import { Button, Card, TextField, Typography } from '@/ui';

import type { DataImportTypeDef } from '@/types/dataImport';
import type { ChangeEvent, FormEvent } from 'react';

import {
  useCreateDataImportMutation,
  useCreateDataImportUploadMutation,
  useGetDataImportQuery,
  useGetDataImportTypesQuery,
  useListDataImportsQuery,
} from '@/store/api/dataImportsApi';
import { isAdmin } from '@/types/user';

/* eslint-disable complexity -- Form combines multiple conditional sections; splitting would add noise */
const StartImportForm = ({
  onCreated,
}: {
  onCreated: (id: string) => void;
}) => {
  const {
    data,
    isLoading: isTypesLoading,
    isError,
    refetch,
  } = useGetDataImportTypesQuery();
  const [createImport, { isLoading }] = useCreateDataImportMutation();
  const [createImportUpload, { isLoading: isUploading }] =
    useCreateDataImportUploadMutation();

  const types = data?.types ?? [];
  const [importType, setImportType] = useState(types[0]?.key ?? '');
  const [sourceType, setSourceType] = useState<'csv' | 'google_sheet'>('csv');
  const [csvFile, setCsvFile] = useState<File | undefined>();
  const [sheetId, setSheetId] = useState('');
  const [sheetRange, setSheetRange] = useState('Sheet1!A:Z');
  const [dryRun, setDryRun] = useState(false);

  useEffect(() => {
    if (!importType && types.length) {
      const first = types[0];
      if (first) setImportType(first.key);
    }
  }, [types.length]);

  const selectedType: DataImportTypeDef | undefined = useMemo(
    () => types.find((t) => t.key === importType),
    [types, importType],
  );

  const sampleHeader = useMemo(() => {
    const headers = selectedType?.expectedFields.map((f) => f.header) ?? [];
    return headers.length ? headers.join(',') : '';
  }, [selectedType]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!importType) return;
    if (sourceType === 'csv') {
      if (!csvFile) return;
      const res = await createImportUpload({
        dryRun,
        file: csvFile,
        importType,
      }).unwrap();
      onCreated(res.id);
    } else {
      if (!sheetId || !sheetRange) return;
      const payload = {
        data_import: {
          google_range: sheetRange,
          google_spreadsheet_id: sheetId,
          import_type: importType,
          options: { dry_run: dryRun },
          source_type: 'google_sheet' as const,
        },
      };
      const res = await createImport(payload).unwrap();
      onCreated(res.id);
    }
  };

  if (isTypesLoading) {
    return (
      <Card className="bg-white p-4">
        <Typography variant="h5">Start an import</Typography>
        <p className="mt-2 text-sm text-cement-500">Loading import types…</p>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card className="bg-white p-4">
        <Typography variant="h5">Start an import</Typography>
        <div className="mt-2 flex items-center gap-3">
          <p className="text-sm text-red-600">Failed to load import types.</p>
          <Button variant="outlined" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  if (types.length === 0) {
    return (
      <Card className="bg-white p-4">
        <Typography variant="h5">Start an import</Typography>
        <p className="mt-2 text-sm text-cement-500">
          No import types available. Check your admin permissions or try again
          later.
        </p>
        <div className="mt-3">
          <Button variant="outlined" onClick={() => refetch()}>
            Refresh
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="bg-white p-4">
      <Typography variant="h5">Start an import</Typography>
      <form className="mt-4 flex flex-col gap-3" onSubmit={onSubmit}>
        <div className="flex flex-col gap-1">
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control -- Label and control are properly associated via htmlFor/id */}
          <label className="text-sm" htmlFor="import-type">
            Import type
          </label>
          <select
            className="rounded border border-sage-300 p-2"
            id="import-type"
            value={importType}
            onChange={(e) => setImportType(e.target.value)}
          >
            {types.map((t) => (
              <option key={t.key} value={t.key}>
                {t.name}
              </option>
            ))}
          </select>
          {selectedType?.description ? (
            <p className="text-xs text-cement-500">
              {selectedType.description}
            </p>
          ) : null}
        </div>

        <div className="flex flex-col gap-2">
          {selectedType ? (
            <div className="rounded border border-sage-200 p-3">
              <Typography variant="subtitle2">Description</Typography>
              {selectedType.description ? (
                <p className="mt-1 text-sm text-cement-600">
                  {selectedType.description}
                </p>
              ) : null}
              {Array.isArray(selectedType.expectedFields) &&
              selectedType.expectedFields.length ? (
                <div className="mt-3">
                  <Typography variant="subtitle2">Expected fields</Typography>
                  <div className="mt-1 overflow-auto rounded border border-sage-200">
                    <table className="w-full table-auto text-sm">
                      <thead>
                        <tr className="bg-sage-50">
                          <th className="px-2 py-1 text-left">Header</th>
                          <th className="px-2 py-1 text-left">Required</th>
                          <th className="px-2 py-1 text-left">Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedType.expectedFields.map((f) => (
                          <tr key={f.header}>
                            <td className="px-2 py-1 font-medium">
                              {f.header}
                            </td>
                            <td className="px-2 py-1">
                              {f.required ? 'Yes' : 'No'}
                            </td>
                            <td className="px-2 py-1 text-cement-600">
                              {f.description ?? ''}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <input
                checked={sourceType === 'csv'}
                id="source-csv"
                name="source"
                type="radio"
                onChange={() => setSourceType('csv')}
              />
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control -- Label and control are properly associated via htmlFor/id */}
              <label className="text-sm" htmlFor="source-csv">
                CSV
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input
                checked={sourceType === 'google_sheet'}
                id="source-google"
                name="source"
                type="radio"
                onChange={() => setSourceType('google_sheet')}
              />
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control -- Label and control are properly associated via htmlFor/id */}
              <label className="text-sm" htmlFor="source-google">
                Google Sheet
              </label>
            </div>
          </div>

          {sourceType === 'csv' ? (
            <div className="flex flex-col gap-1">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control -- Label and control are properly associated via htmlFor/id */}
              <label className="text-sm" htmlFor="csv-file">
                CSV file
              </label>
              <input
                accept=".csv,text/csv"
                id="csv-file"
                type="file"
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setCsvFile(e.target.files?.[0])
                }
              />
              {sampleHeader ? (
                <p className="text-xs text-cement-500">
                  Expected headers: {sampleHeader}
                </p>
              ) : null}
            </div>
          ) : (
            <div className="flex flex-col gap-2 md:flex-row">
              <TextField
                className="flex-1"
                label="Spreadsheet ID"
                value={sheetId}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setSheetId(e.target.value)
                }
              />
              <TextField
                className="flex-1"
                label="Range"
                value={sheetRange}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setSheetRange(e.target.value)
                }
              />
            </div>
          )}
        </div>

        <div className="mt-2 flex items-center gap-2 text-sm">
          <input
            checked={dryRun}
            id="dry-run"
            type="checkbox"
            onChange={(e) => setDryRun(e.target.checked)}
          />
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control -- Label and control are properly associated via htmlFor/id */}
          <label htmlFor="dry-run">Dry run</label>
        </div>

        <div>
          <Button
            disabled={
              isLoading ||
              isUploading ||
              !importType ||
              (sourceType === 'csv' && !csvFile)
            }
            type="submit"
          >
            {isLoading || isUploading ? 'Submitting…' : 'Submit import'}
          </Button>
        </div>
      </form>
    </Card>
  );
};
/* eslint-enable complexity */

const JobStatusPanel = ({ jobId }: { jobId?: string }) => {
  const { data: job } = useGetDataImportQuery(jobId ?? '', {
    pollingInterval: jobId ? 2500 : 0,
    skip: !jobId,
  });

  if (!jobId) return null;

  const completed = job?.status === 'completed' || job?.status === 'failed';

  return (
    <Card className="bg-white p-4">
      <Typography variant="h5">Current import</Typography>
      {!job ? (
        <p className="mt-2 text-sm text-cement-500">Loading…</p>
      ) : (
        <div className="mt-3 space-y-2">
          <div className="text-sm">
            Status: <span className="font-medium">{job.status}</span>
          </div>
          {completed && job.resultSummary ? (
            <div className="space-y-1 text-sm">
              {'name' in job.resultSummary && job.resultSummary.name ? (
                <div>Import: {String(job.resultSummary.name)}</div>
              ) : null}
              <div>
                Processed: {job.resultSummary.processedRows ?? 0} /{' '}
                {job.resultSummary.totalRows ?? 0}
              </div>
              <div>
                Created: {job.resultSummary.createdCount ?? 0} | Updated:{' '}
                {job.resultSummary.updatedCount ?? 0} | Skipped:{' '}
                {job.resultSummary.skippedCount ?? 0} | Errors:{' '}
                {job.resultSummary.errorCount ?? 0}
              </div>
              {Array.isArray(job.resultSummary.errors) &&
              job.resultSummary.errors.length ? (
                <div className="mt-2">
                  <details>
                    <summary className="cursor-pointer">
                      View errors ({job.resultSummary.errors.length})
                    </summary>
                    <div className="mt-2 max-h-64 overflow-auto rounded border border-sage-200 p-2 text-xs">
                      <table className="w-full table-auto">
                        <thead>
                          <tr>
                            <th className="px-2 py-1 text-left">Index</th>
                            <th className="px-2 py-1 text-left">Message</th>
                            <th className="px-2 py-1 text-left">Class</th>
                          </tr>
                        </thead>
                        <tbody>
                          {job.resultSummary.errors.map((err) => (
                            <tr
                              key={`${err.index}-${err.class ?? ''}-${err.message}`}
                            >
                              <td className="px-2 py-1">{err.index}</td>
                              <td className="px-2 py-1">{err.message}</td>
                              <td className="px-2 py-1">{err.class ?? ''}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                </div>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-cement-500">Polling until completion…</p>
          )}
        </div>
      )}
    </Card>
  );
};

const RecentImports = ({ onSelect }: { onSelect: (id: string) => void }) => {
  const { data } = useListDataImportsQuery();
  const items = data ?? [];
  return (
    <Card className="bg-white p-4">
      <Typography variant="h5">Recent imports</Typography>
      <div className="mt-3 divide-y divide-sage-200">
        {items.length === 0 ? (
          <p className="text-sm text-cement-500">No recent imports</p>
        ) : (
          items.map((it) => (
            <button
              key={it.id}
              className="hover:bg-sage-50 flex w-full items-center justify-between p-2 text-left"
              type="button"
              onClick={() => onSelect(it.id)}
            >
              <div className="flex flex-col">
                <span className="text-sm font-medium">{it.importType}</span>
                <span className="text-xs text-cement-500">{it.status}</span>
              </div>
              <span className="text-xs text-cement-400">
                {it.createdAt ?? ''}
              </span>
            </button>
          ))
        )}
      </div>
    </Card>
  );
};

const DataImports = () => {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const [jobId, setJobId] = useState<string | undefined>();

  useEffect(() => {
    if (user.id && !isAdmin(user)) navigate('/');
  }, [user.id, user.roles]);

  return (
    <div className="container mx-auto max-w-screen-lg p-4">
      <Typography variant="h4">Data Imports</Typography>
      <div className="mt-4 grid grid-cols-1 gap-4">
        <StartImportForm onCreated={setJobId} />
        <JobStatusPanel jobId={jobId} />
        <RecentImports onSelect={setJobId} />
      </div>
    </div>
  );
};

export default DataImports;
